import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api } from "../api/client";
import type { Dashboard } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import { ErrorBanner, LoadingState, Mono, PageHeader, RoleGate, ago } from "../components/common";

/**
 * Opening screen: what came in, where it went, and whether anything upstream is
 * broken. Everything here is derived from the same views the SQL runbook uses, so
 * the console cannot report a different number than `psql` would.
 */
export function DashboardPage() {
  const { can } = useAuth();
  const board = useResource<Dashboard>("/api/dashboard?days=7");
  const runPipeline = useMutation(async () => {
    await api.post("/api/pipeline/run");
  });

  if (board.loading && !board.data) return <LoadingState label="Loading dashboard…" />;
  if (board.error) return <ErrorBanner error={board.error} onRetry={board.reload} />;
  if (!board.data) return null;

  const { leads, sourceHealth, signalCatalog, funnel, pipeline } = board.data;
  const totals = leads.totals;
  const stalledSources = sourceHealth.filter((s) => s.last_status === "error");
  const activeSignals = signalCatalog.filter((s) => s.active === "true").length;

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle="Lead flow, source health and the trial scoreboard."
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={board.reload}>
              Refresh
            </Button>
            <RoleGate allowed={can("operator")} needs="operator">
              <Tooltip title="Runs signals → resolve → enrich → assemble → route → deliver. Enrichment spends metered provider quota.">
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => void runPipeline.run(undefined).then((ok) => ok && board.reload())}
                  disabled={runPipeline.pending}
                >
                  {runPipeline.pending ? "Enqueueing…" : "Run pipeline"}
                </Button>
              </Tooltip>
            </RoleGate>
          </>
        }
      />

      <ErrorBanner error={runPipeline.error} />

      {stalledSources.length > 0 ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {stalledSources.length} source{stalledSources.length === 1 ? "" : "s"} failed on the last run:{" "}
          {stalledSources.map((s) => s.source).join(", ")}.{" "}
          <Link component={RouterLink} to="/sources">Open data sources</Link>
        </Alert>
      ) : null}

      {!pipeline.scheduleEnabled ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The full pipeline has no schedule (<Mono>PIPELINE_SCHEDULE</Mono> is empty), so it only runs when
          someone asks. Ingest still runs on each source's own cron.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 2,
          mb: 3,
        }}
      >
        <Stat label="Leads, all time" value={totals.total} />
        <Stat label="Event in last 7d" value={totals.last_7d} />
        <Stat label="Event in last 30d" value={totals.last_30d} />
        <Stat label="Assigned" value={totals.assigned} />
        <Stat label="Delivered" value={totals.delivered} />
        <Stat
          label="Delivery failures"
          value={totals.delivery_errors}
          tone={totals.delivery_errors > 0 ? "error" : "default"}
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Leads by quality
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              {leads.byQuality.map((row) => (
                <TableRow key={row.quality}>
                  <TableCell sx={{ border: 0 }}>
                    <Link
                      component={RouterLink}
                      to={`/leads?quality=${encodeURIComponent(row.quality)}`}
                      color="primary.main"
                      underline="always"
                      sx={{
                        "&:hover": { color: "primary.light" },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.light",
                          outlineOffset: 2,
                          borderRadius: 1,
                        },
                      }}
                    >
                      {row.quality}
                    </Link>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    {row.leads}
                  </TableCell>
                </TableRow>
              ))}
              {leads.byQuality.length === 0 ? (
                <TableRow>
                  <TableCell sx={{ border: 0 }} colSpan={2}>
                    No leads assembled yet.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Configuration
          </Typography>
          <Table size="small" sx={{ mt: 1 }}>
            <TableBody>
              <ConfigRow label="Active signals" value={`${activeSignals} of ${signalCatalog.length}`} />
              <ConfigRow label="Pipeline cron" value={pipeline.schedule ?? "disabled"} />
              <ConfigRow label="Enrichment cap per run" value={String(pipeline.enrichMaxCalls)} />
              <ConfigRow label="Ingest pages per run" value={String(pipeline.ingestMaxPages)} />
              <ConfigRow
                label="Enrichment providers"
                value={
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {pipeline.providers.fakeEnrichment ? (
                      <Tooltip title="FC_FAKE_ENRICH=1: contacts are synthetic and marked unverified, so they can never ship as real.">
                        <Chip label="fake" color="warning" variant="outlined" />
                      </Tooltip>
                    ) : null}
                    <Chip
                      label="hunter"
                      color={pipeline.providers.hunter ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      label="places"
                      color={pipeline.providers.googlePlaces ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      label="socrata token"
                      color={pipeline.providers.socrataAppToken ? "success" : "default"}
                      variant="outlined"
                    />
                  </Stack>
                }
              />
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Whether a key is configured, never its value.
          </Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Funnel, last {funnel.days} days — current state per lead, not cumulative steps
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Market</TableCell>
              <TableCell align="right">Delivered</TableCell>
              <TableCell align="right">No feedback</TableCell>
              <TableCell align="right">Contacted</TableCell>
              <TableCell align="right">Quoted</TableCell>
              <TableCell align="right">Won</TableCell>
              <TableCell align="right">Bad</TableCell>
              <TableCell align="right">Won value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {funnel.rows.map((row) => (
              <TableRow key={`${row.client}-${row.market}`}>
                <TableCell>{row.client}</TableCell>
                <TableCell>{row.market}</TableCell>
                <TableCell align="right">{row.delivered}</TableCell>
                <TableCell align="right">{row.delivered - row.reported}</TableCell>
                <TableCell align="right">{row.contacted}</TableCell>
                <TableCell align="right">{row.quoted}</TableCell>
                <TableCell align="right">{row.won}</TableCell>
                <TableCell align="right">{row.badLead}</TableCell>
                <TableCell align="right">
                  {row.wonValueUsd > 0 ? `$${row.wonValueUsd.toLocaleString("en-US")}` : "—"}
                </TableCell>
              </TableRow>
            ))}
            {funnel.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>Nothing delivered in this window.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Source health
        </Typography>
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Connector</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sourceHealth.map((s) => (
              <TableRow key={s.source}>
                <TableCell>
                  <Link component={RouterLink} to="/sources" underline="hover">{s.source}</Link>
                </TableCell>
                <TableCell>{s.connector_type}</TableCell>
                <TableCell>
                  <Mono>{s.schedule ?? "manual"}</Mono>
                </TableCell>
                <TableCell>{ago(s.last_run)}</TableCell>
                <TableCell align="right">{s.last_rows ?? "—"}</TableCell>
                <TableCell>
                  {s.active !== "true" ? (
                    <Chip label="paused" variant="outlined" />
                  ) : s.last_status ? (
                    <Chip
                      label={s.last_status}
                      color={s.last_status === "ok" ? "success" : "error"}
                      variant="outlined"
                    />
                  ) : (
                    <Chip label="never run" variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sourceHealth.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>No data sources configured.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "error";
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h5" color={tone === "error" ? "error.main" : "text.primary"}>
        {value.toLocaleString("en-US")}
      </Typography>
    </Paper>
  );
}

function ConfigRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, color: "text.secondary", width: 200 }}>{label}</TableCell>
      <TableCell sx={{ border: 0 }}>{value}</TableCell>
    </TableRow>
  );
}
